import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useSectionTheme } from "@/context/BrandingContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Briefcase as BriefcaseIcon, Bookmark, Eye, MessageSquare, Sparkles, AlertTriangle, RefreshCw, Loader2 } from "lucide-react";
import ResumePreviewDialog from "@/components/ResumePreviewDialog";
import CandidateProfileDialog from "@/components/CandidateProfileDialog";
import CandidateVoteBar from "@/components/CandidateVoteBar";
import ResumeThumb from "@/components/ResumeThumb";
import { formatCompCurrency, budgetBadgeClass, budgetLabel } from "@/lib/budget";
import DataTable from "@/components/DataTable";
import CandidatesListTable from "@/components/CandidatesListTable";
import BulkInterviewAssignDialog from "@/components/BulkInterviewAssignDialog";
import BulkPushSkillsDialog from "@/components/BulkPushSkillsDialog";
import {
    BulkAssignButton,
    BulkAddToListButton,
    BulkAnalyzeButton,
    BulkRetryButton,
} from "@/components/candtable/CandidateBulkButtons";
import AvailabilityPill from "@/components/candidate/AvailabilityPill";
import TrackButton from "@/components/TrackButton";
import ResumePreviewButton from "@/components/ResumePreviewButton";

const SORT_OPTIONS = [
    ["recent", "Most recently updated"],
    ["oldest", "Oldest first"],
    ["quality", "Profile quality"],
    ["experience", "Experience (YoE)"],
    ["skills", "Skills count"],
    ["name", "Name (A–Z)"],
];

export default function CandidatesList() {
    useSectionTheme("candidates_list");
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [filters, setFilters] = useState({ q: "", skill: "", location: "", min_exp: "", max_exp: "", sort: "recent", budget: "" });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [loading, setLoading] = useState(true);
    const [picked, setPicked] = useState(new Set());
    const [showBulkInterview, setShowBulkInterview] = useState(false);
    const [showBulkSkills, setShowBulkSkills] = useState(false);
    const [previewCand, setPreviewCand] = useState(null);
    const [profilePreviewId, setProfilePreviewId] = useState(null);
    // B8 — view mode toggle. `excel` renders the new CandidatesListTable
    // (workspace-wide Excel-style grid); `classic` keeps the legacy DataTable
    // with its inline editing + custom actions. Persisted per user.
    const [viewMode, setViewMode] = useState(() => {
        try { return localStorage.getItem("pw:candidates:viewMode") || "classic"; }
        catch { return "classic"; }
    });
    useEffect(() => {
        try { localStorage.setItem("pw:candidates:viewMode", viewMode); }
        catch { /* noop */ }
    }, [viewMode]);
    const navigate = useNavigate();

    const load = async (overrides = {}) => {
        setLoading(true);
        try {
            const params = { page, page_size: pageSize };
            for (const [k, v] of Object.entries(filters)) {
                if (v !== "" && v !== null && v !== undefined) params[k] = v;
            }
            Object.assign(params, overrides);
            const { data } = await api.get("/candidates", { params });
            if (Array.isArray(data)) { setItems(data); setTotal(data.length); }
            else { setItems(data.items || []); setTotal(data.total || 0); }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.sort, filters.budget, page, pageSize]);

    // Phase 56.2 — Real-time row updates via the global AI-Queue event.
    // When a parse finishes anywhere in the tenant, the AIQueueWidget dispatches
    // `ai-queue-completed` with the candidate_id. We patch the matching row
    // (processing_status → done) and trigger a brief sparkle/pulse animation.
    // For failures, we also patch the row to `failed` so the Retry chip
    // surfaces without a full refresh.
    const [sparkleIds, setSparkleIds] = useState(new Set());
    useEffect(() => {
        const handler = async (e) => {
            const detail = e?.detail || {};
            const cid = detail.candidate_id;
            if (!cid) return;
            // Best-effort patch — fetch the lightweight processing status so
            // we get the authoritative state (done / failed) and any error.
            try {
                const { data } = await api.get(`/candidates/${cid}/processing-status`);
                setItems((prev) => prev.map((c) => c.id === cid ? { ...c, ...data } : c));
            } catch {
                // If the endpoint fails (e.g. cross-tenant), still flip the row optimistically
                setItems((prev) => prev.map((c) => c.id === cid ? { ...c, processing_status: "done" } : c));
            }
            // Trigger the sparkle for 2.5s
            setSparkleIds((s) => {
                const n = new Set(s);
                n.add(cid);
                return n;
            });
            setTimeout(() => {
                setSparkleIds((s) => {
                    const n = new Set(s);
                    n.delete(cid);
                    return n;
                });
            }, 2500);
        };
        window.addEventListener("ai-queue-completed", handler);
        return () => window.removeEventListener("ai-queue-completed", handler);
    }, []);

    // Phase 55.3 — auto-refresh REMOVED.
    // The whole-list 4s poll while any row was 'processing' was a constant
    // source of UX friction (full table re-render, lost scroll/selection,
    // flickering chips). Recruiters now control refresh manually via the
    // explicit Refresh button in the toolbar, which also surfaces a "N
    // processing" counter so they know there's pending work without the
    // list reloading itself.
    const processingCount = items.filter((c) => c.processing_status === "processing").length;

    const reparseCandidate = async (cid) => {
        try {
            await api.post(`/candidates/${cid}/reparse`);
            toast.success("Re-parsing started — refresh in a few seconds");
            // Optimistic flip so the chip updates immediately
            setItems((prev) => prev.map((c) => c.id === cid ? { ...c, processing_status: "processing", parse_error: null } : c));
        } catch (err) { toast.error(formatApiError(err)); }
    };

    /** Phase 55.4 — Per-row status refresh. Hits the lightweight
     * `/candidates/{cid}/processing-status` endpoint and patches just that
     * row in the items array — the rest of the list never re-renders.
     */
    const refreshRow = async (cid) => {
        try {
            const { data } = await api.get(`/candidates/${cid}/processing-status`);
            setItems((prev) => prev.map((c) => c.id === cid ? { ...c, ...data } : c));
            if (data.processing_status === "processing") toast.info("Still processing…");
        } catch (err) { toast.error(formatApiError(err)); }
    };


    const onSubmit = (e) => { e.preventDefault(); setPage(1); load({ page: 1 }); };
    const toggle = (id) => {
        const s = new Set(picked);
        if (s.has(id)) s.delete(id); else s.add(id);
        setPicked(s);
    };
    const toggleAll = (checked) => {
        if (checked) setPicked(new Set(items.map((c) => c.id)));
        else setPicked(new Set());
    };

    // Phase R6 · select-all matching across filter results (not just this page)
    const [selectingAllMatching, setSelectingAllMatching] = useState(false);
    const selectAllMatching = async () => {
        setSelectingAllMatching(true);
        try {
            const params = { ids_only: 1 };
            for (const [k, v] of Object.entries(filters)) {
                if (v !== "" && v !== null && v !== undefined) params[k] = v;
            }
            const { data } = await api.get("/candidates", { params });
            const ids = data?.ids || [];
            setPicked(new Set(ids));
            toast.success(`Selected ${ids.length.toLocaleString()} matching candidates`);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSelectingAllMatching(false); }
    };
    const clearPicked = () => setPicked(new Set());

    const updateCandidate = async (cand, patch) => {
        const prev = items;
        setItems((arr) => arr.map((c) => (c.id === cand.id ? { ...c, ...patch } : c)));
        try {
            const { data } = await api.patch(`/candidates/${cand.id}`, patch);
            setItems((arr) => arr.map((c) => (c.id === cand.id ? { ...c, ...data } : c)));
            toast.success("Saved");
        } catch (err) {
            toast.error(formatApiError(err));
            setItems(prev);
        }
    };

    const onCellEdit = async ({ row, column, value }) => {
        const numeric = ["current_salary", "desired_salary_min", "desired_salary_max", "total_experience_years", "notice_period_days"];
        let v = value;
        if (numeric.includes(column.key)) {
            v = (v === "" || v === null) ? null : Number(v);
            if (v !== null && Number.isNaN(v)) { toast.error("Must be a number"); return; }
        }
        if (column.key === "skills") {
            // skills is an array — accept comma-separated input; cap at 50 to prevent paste abuse
            v = String(value || "").split(",").map((s) => s.trim()).filter(Boolean).slice(0, 50);
        }
        await updateCandidate(row, { [column.key]: v });
    };

    const columns = useMemo(() => [
        {
            key: "_pick", label: "", width: 36, filterable: false, sortable: false, noRowClick: true,
            render: (c) => (
                <Checkbox
                    checked={picked.has(c.id)}
                    onCheckedChange={() => toggle(c.id)}
                    className="rounded-none"
                    onClick={(e) => e.stopPropagation()}
                    data-testid={`pick-${c.id}`}
                />
            ),
        },
        {
            key: "name", label: "Name", width: 260, sortable: true, editable: true,
            render: (c) => (
                <div className="flex items-start gap-2 min-w-0">
                    <ResumeThumb
                        candidateId={c.id}
                        filename={c.resume_filename}
                        size={44}
                        onClick={c.resume_filename ? () => setPreviewCand(c) : undefined}
                    />
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 min-w-0">
                        <div className="font-medium text-foreground truncate">{c.name || "Unknown"}</div>
                        <TrackButton
                            candidateId={c.id}
                            variant="icon"
                            testId={`cand-track-${c.id}`}
                            className="shrink-0"
                        />
                        <ResumePreviewButton
                            candidate={c}
                            testId={`cand-preview-${c.id}`}
                            className="shrink-0"
                        />
                        {sparkleIds.has(c.id) && (
                            <span
                                className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-emerald-300 bg-emerald-50 text-emerald-800 inline-flex items-center gap-1 shrink-0 animate-pulse"
                                title="AI just finished parsing this resume"
                                data-testid={`cand-sparkle-${c.id}`}
                            >
                                <span aria-hidden>✨</span> Just parsed
                            </span>
                        )}
                        {c.processing_status === "processing" && (
                            <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-800 inline-flex items-center gap-1 shrink-0" title="AI is parsing this resume — refresh in a few seconds" data-testid={`cand-processing-${c.id}`}>
                                <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" /> Processing
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); refreshRow(c.id); }}
                                    className="ml-1 -mr-0.5 hover:bg-amber-200 p-0.5 rounded"
                                    title="Check this row's status now"
                                    data-testid={`cand-row-refresh-${c.id}`}
                                    aria-label="Refresh this row"
                                >
                                    <RefreshCw className="w-2.5 h-2.5" />
                                </button>
                            </span>
                        )}
                        {c.processing_status === "failed" && (
                            <button onClick={(e) => { e.stopPropagation(); reparseCandidate(c.id); }} className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100 shrink-0" title={c.parse_error || "Parsing failed — click to retry"} data-testid={`cand-retry-${c.id}`}>
                                Retry parse
                            </button>
                        )}
                        {c.processing_status === "ai_unavailable" && (
                            <button onClick={(e) => { e.stopPropagation(); reparseCandidate(c.id); }} className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 shrink-0 inline-flex items-center gap-1" title={c.parse_error || "AI service was paused — click to retry once credits restored"} data-testid={`cand-ai-retry-${c.id}`}>
                                AI paused · Retry
                            </button>
                        )}
                        {c.processing_status === "ai_off" && (
                            <button onClick={(e) => { e.stopPropagation(); reparseCandidate(c.id); }} className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-ikb/40 bg-ikb/5 text-ikb hover:bg-ikb/10 shrink-0 inline-flex items-center gap-1" title="Auto-parsing is OFF — click to run AI on this candidate now" data-testid={`cand-run-ai-${c.id}`}>
                                <span aria-hidden>✦</span> Run AI
                            </button>
                        )}
                    </div>
                        <div className="text-[11px] font-mono text-foreground/60 truncate mt-0.5 flex items-center gap-1.5">
                            <span className={c.email_bounced ? "line-through text-rose-700" : ""}>{c.email || "no email"}</span>
                            {c.email_bounced && (
                                <span
                                    className="text-[9px] font-mono uppercase tracking-widest px-1 py-0.5 border border-rose-400 bg-rose-50 text-rose-700 inline-flex items-center gap-0.5 shrink-0"
                                    title={c.email_bounce_reason || "This address bounced on the last send"}
                                    data-testid={`cand-row-bounced-${c.id}`}
                                >
                                    ⚠ Bounced
                                </span>
                            )}
                        </div>
                        {(c.jobs || []).length > 0 && (c.jobs[0]?.application_id) && (
                            <div className="mt-1.5" onClick={(e) => e.stopPropagation()} data-testid={`cand-row-votebar-${c.id}`}>
                                <CandidateVoteBar
                                    applicationId={c.jobs[0].application_id}
                                    initial={c.jobs[0].vote_summary ? { counts: c.jobs[0].vote_summary.counts || {}, my_vote: null, voters: [] } : undefined}
                                    size="sm"
                                />
                            </div>
                        )}
                    </div>
                </div>
            ),
        },
        {
            key: "current_title", label: "Title", width: 180, sortable: true, editable: true,
            render: (c) => (
                <div className="min-w-0">
                    <div className="text-sm truncate">{c.current_title || "—"}</div>
                    {c.current_company && <div className="text-[11px] font-mono text-foreground/60 truncate">{c.current_company}</div>}
                </div>
            ),
        },
        { key: "location", label: "Location", width: 120, sortable: true, editable: true },
        {
            key: "total_experience_years", label: "YoE", width: 65, sortable: true, editable: true, align: "right",
            render: (c, v) => <span className="font-mono text-xs">{v ?? 0}</span>,
        },
        {
            key: "earliest_join_date", label: "Joining", width: 120, sortable: false, editable: false,
            render: (c) => <AvailabilityPill candidate={c} testIdPrefix={`candlist-avail-${c.id}`} />,
        },
        {
            key: "quality_score", label: "Q", width: 50, sortable: true, align: "right",
            render: (c) => <span className="font-mono text-xs">{c.quality_score ?? 0}</span>,
        },
        {
            key: "skills", label: "Skills", width: 240, sortable: true, editable: true,
            accessor: (c) => (c.skills || []).join(", "),
            render: (c) => (
                <div className="flex flex-wrap gap-1">
                    {(c.skills || []).slice(0, 4).map((s) => (
                        <span key={s} className="text-[10px] font-mono uppercase tracking-widest border border-border px-1.5 py-0.5">{s}</span>
                    ))}
                    {(c.skills || []).length > 4 && <span className="text-[10px] font-mono text-foreground/60">+{c.skills.length - 4}</span>}
                </div>
            ),
        },
        {
            key: "current_salary", label: "Current", width: 110, sortable: true, editable: true, align: "right",
            render: (c, v) => v ? <span className="font-mono text-xs">{formatCompCurrency(v, c.salary_currency, c.salary_period)}</span> : <span className="text-foreground/30">—</span>,
        },
        {
            key: "desired_salary_min", label: "Desired min", width: 110, sortable: true, editable: true, align: "right",
            render: (c, v) => v ? <span className="font-mono text-xs">{formatCompCurrency(v, c.salary_currency, c.salary_period)}</span> : <span className="text-foreground/30">—</span>,
        },
        {
            key: "desired_salary_max", label: "Desired max", width: 110, sortable: true, editable: true, align: "right",
            render: (c, v) => v ? <span className="font-mono text-xs">{formatCompCurrency(v, c.salary_currency, c.salary_period)}</span> : <span className="text-foreground/30">—</span>,
        },
        {
            key: "budget_summary_best", label: "Budget", width: 110, sortable: true, filterable: true,
            accessor: (c) => c.budget_summary?.best || "",
            render: (c) => c.budget_summary?.best ? (
                <span
                    className={`px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-widest border ${budgetBadgeClass(c.budget_summary.best)}`}
                    title={`Across ${c.budget_summary.evaluated} application${c.budget_summary.evaluated === 1 ? "" : "s"}: ${c.budget_summary.at} in · ${c.budget_summary.under} under · ${c.budget_summary.over} over`}
                    data-testid={`cand-budget-summary-${c.id}`}
                >
                    {budgetLabel(c.budget_summary.best)}
                </span>
            ) : <span className="text-foreground/30">—</span>,
        },
        {
            key: "jobs", label: "Applied to", width: 220, filterable: true,
            accessor: (c) => (c.jobs || []).map((j) => j.job_title).join(", "),
            render: (c) => (
                <div className="flex flex-wrap gap-1">
                    {(c.jobs || []).slice(0, 2).map((j) => (
                        <Link
                            key={j.job_id}
                            to={`/jobs/${j.job_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest border border-ikb/30 bg-blue-50/50 text-ikb px-1.5 py-0.5 hover:bg-blue-100"
                            title={`Stage: ${j.stage || "Applied"} · Match ${j.match_score || 0}/100`}
                        >
                            {j.job_title}
                        </Link>
                    ))}
                    {(c.jobs || []).length > 2 && <span className="text-[10px] font-mono text-foreground/60">+{c.jobs.length - 2}</span>}
                </div>
            ),
        },
        {
            key: "created_at", label: "Added", width: 90, sortable: true, filterable: false,
            render: (c, v) => {
                if (!v) return <span className="text-foreground/30">—</span>;
                const d = new Date(v); if (Number.isNaN(d.getTime())) return <span className="text-foreground/30">—</span>;
                return <span className="font-mono text-xs">{d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>;
            },
        },
        {
            key: "created_by_name", label: "Added by", width: 130, sortable: true,
            accessor: (c) => c.created_by_name || c.resume_uploaded_by_name || "",
            render: (c, v) => v ? <span className="text-xs truncate">{v}</span> : <span className="text-foreground/30">—</span>,
        },
    ], [picked, sparkleIds]);

    const renderActions = (c) => (
        <>
            <button
                type="button"
                onClick={() => setProfilePreviewId(c.id)}
                className="p-1 border border-border hover:bg-blue-50 hover:border-ikb hover:text-ikb inline-flex"
                title="Preview profile + AI analysis"
                data-testid={`candidate-action-preview-${c.id}`}
            >
                <Eye className="w-3 h-3" />
            </button>
            <Link to={`/candidates/${c.id}#notes`} className="p-1 border border-border hover:bg-secondary inline-flex" title="Notes" data-testid={`candidate-action-notes-${c.id}`}>
                <MessageSquare className="w-3 h-3" />
            </Link>
        </>
    );

    const toolbarExtra = (
        <div className="flex items-center gap-1.5 flex-wrap">
            <BulkAssignButton candidateIds={[...picked]} onDone={() => { setPicked(new Set()); load(); }} />
            <BulkAddToListButton candidateIds={[...picked]} onDone={() => setPicked(new Set())} />
            <Button
                onClick={() => setShowBulkInterview(true)}
                disabled={picked.size === 0}
                variant="outline" size="sm"
                className="rounded-none h-8 border-ikb/40 text-ikb hover:bg-ikb/5"
                data-testid="bulk-interview-btn"
                title="Create interview rounds for selected candidates with one interviewer"
            >
                <BriefcaseIcon className="w-3.5 h-3.5 mr-1" />
                Interview · {picked.size}
            </Button>
            <Button
                onClick={() => setShowBulkSkills(true)}
                disabled={picked.size === 0}
                variant="outline" size="sm"
                className="rounded-none h-8 border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                data-testid="bulk-skills-btn"
                title="Push rating skills to upcoming + future interviews for selected candidates"
            >
                <Sparkles className="w-3.5 h-3.5 mr-1" />
                Skills · {picked.size}
            </Button>
            <BulkAnalyzeButton candidateIds={[...picked]} onDone={() => { setPicked(new Set()); load(); }} />
            <BulkRetryButton candidateIds={[...picked]} items={items} onDone={() => { setPicked(new Set()); load(); }} />
            <ClearStuckButton onDone={() => load()} />
            <RefreshButton processingCount={processingCount} loading={loading} onClick={() => load()} />
        </div>
    );

    return (
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-4 sm:space-y-5">
            <div>
                <div className="text-xs uppercase tracking-widest font-mono text-ikb">Candidate database</div>
                <h1 className="font-display font-black text-2xl sm:text-4xl mt-2">All candidates</h1>
                <p className="hidden sm:block text-xs text-muted-foreground font-mono mt-1">Double-click any cell to edit · Press Esc to cancel · Click headers to sort · Tap Fullscreen for large views</p>
            </div>

            <form onSubmit={onSubmit} className="border border-border bg-white p-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-2">
                <div className="md:col-span-4 relative sm:col-span-2">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Server search (name, email, title, skills)..." className="rounded-none pl-10 h-10" data-testid="candidates-search-q" />
                </div>
                <Input value={filters.skill} onChange={(e) => setFilters({ ...filters, skill: e.target.value })} placeholder="Exact skill" className="rounded-none h-10 md:col-span-2" data-testid="candidates-filter-skill" />
                <Input value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })} placeholder="Location" className="rounded-none h-10 md:col-span-2" data-testid="candidates-filter-location" />
                <Input value={filters.min_exp} onChange={(e) => setFilters({ ...filters, min_exp: e.target.value })} type="number" min={0} placeholder="Min YoE" className="rounded-none h-10 md:col-span-1" data-testid="candidates-filter-min" />
                <Input value={filters.max_exp} onChange={(e) => setFilters({ ...filters, max_exp: e.target.value })} type="number" min={0} placeholder="Max YoE" className="rounded-none h-10 md:col-span-1" data-testid="candidates-filter-max" />
                <select
                    value={filters.budget}
                    onChange={(e) => { setFilters({ ...filters, budget: e.target.value }); setPage(1); }}
                    className="h-10 px-2 border border-border bg-white text-sm md:col-span-1"
                    data-testid="candidates-budget-filter"
                >
                    <option value="">Budget</option>
                    <option value="at">In</option>
                    <option value="under">Under</option>
                    <option value="over">Over</option>
                    <option value="missing">No data</option>
                </select>
                <Button type="submit" className="rounded-none h-10 bg-ikb hover:bg-[#001f7a] text-white sm:col-span-2 md:col-span-1" data-testid="candidates-apply-filters">Apply</Button>
            </form>

            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 text-sm">
                    <Checkbox
                        checked={items.length > 0 && picked.size === items.length}
                        onCheckedChange={(v) => toggleAll(!!v)}
                        className="rounded-none"
                        data-testid="select-all-candidates"
                    />
                    <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                        {picked.size > 0 ? `${picked.size} selected` : `${total.toLocaleString()} candidates`}
                    </span>
                    {picked.size === items.length && items.length > 0 && total > items.length && picked.size < total && (
                        <button
                            type="button"
                            onClick={selectAllMatching}
                            disabled={selectingAllMatching}
                            className="text-xs font-mono uppercase tracking-widest text-ikb hover:underline disabled:opacity-50"
                            data-testid="select-all-matching-candidates"
                        >
                            {selectingAllMatching ? "Selecting…" : `Select all ${total.toLocaleString()} matching →`}
                        </button>
                    )}
                    {picked.size > items.length && (
                        <button type="button" onClick={clearPicked} className="text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground hover:underline" data-testid="clear-selection-candidates">
                            Clear selection
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Sort:</label>
                    <select
                        value={filters.sort}
                        onChange={(e) => setFilters({ ...filters, sort: e.target.value })}
                        className="h-9 px-2 border border-border bg-white text-sm"
                        data-testid="candidates-sort"
                    >
                        {SORT_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                    </select>
                    <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Per page:</label>
                    <select
                        value={pageSize}
                        onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}
                        className="h-9 px-2 border border-border bg-white text-sm"
                        data-testid="candidates-page-size"
                    >
                        {[50, 100, 200, 500].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                </div>
            </div>

            {viewMode === "excel" ? (
                <div className="border border-border bg-secondary/10 px-3 py-2 mb-2 flex items-center gap-2">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">View</span>
                    <div className="inline-flex items-center gap-0 border border-border">
                        <button type="button" onClick={() => setViewMode("classic")}
                            className="h-7 px-2 text-[10px] font-mono uppercase tracking-widest hover:text-ikb"
                            data-testid="candidates-view-classic">Classic</button>
                        <button type="button" onClick={() => setViewMode("excel")}
                            className="h-7 px-2 text-[10px] font-mono uppercase tracking-widest bg-ikb text-white"
                            data-testid="candidates-view-excel">Excel</button>
                    </div>
                    <span className="text-[10px] font-mono text-muted-foreground ml-2">
                        Sortable · searchable · CSV export · bulk actions · compare up to 10
                    </span>
                </div>
            ) : null}

            {viewMode === "classic" ? (
                <DataTable
                    columns={columns}
                    rows={items}
                    rowKey={(r) => r.id}
                    onRowClick={(r) => navigate(`/candidates/${r.id}`)}
                    onCellEdit={onCellEdit}
                    actions={renderActions}
                    storageKey="candidates"
                    testId="candidates-table"
                    freezeFirstColumn={2}
                    loading={loading}
                    emptyMessage="No candidates yet. Upload resumes from a job to grow your database."
                    toolbarExtra={
                        <div className="inline-flex items-center gap-0 border border-border">
                            <button type="button" onClick={() => setViewMode("classic")}
                                className="h-7 px-2 text-[10px] font-mono uppercase tracking-widest bg-ikb text-white"
                                data-testid="candidates-view-classic">Classic</button>
                            <button type="button" onClick={() => setViewMode("excel")}
                                className="h-7 px-2 text-[10px] font-mono uppercase tracking-widest hover:text-ikb"
                                data-testid="candidates-view-excel">Excel</button>
                            {toolbarExtra}
                        </div>
                    }
                />
            ) : (
                <div data-testid="candidates-excel-view">
                    <CandidatesListTable
                        candidates={items}
                        onOpenProfile={(c) => setProfilePreviewId(c.id)}
                        onRefresh={() => load()}
                    />
                </div>
            )}

            {previewCand && (
                <ResumePreviewDialog
                    open={!!previewCand}
                    onClose={() => setPreviewCand(null)}
                    candidate={previewCand}
                />
            )}
            <BulkInterviewAssignDialog
                open={showBulkInterview}
                candidateIds={[...picked]}
                onClose={() => setShowBulkInterview(false)}
                onDone={() => { setPicked(new Set()); load(); }}
            />
            <BulkPushSkillsDialog
                open={showBulkSkills}
                candidateIds={[...picked]}
                onClose={() => setShowBulkSkills(false)}
                onDone={() => { setPicked(new Set()); load(); }}
            />
            {profilePreviewId && (
                <CandidateProfileDialog
                    open={!!profilePreviewId}
                    onClose={() => setProfilePreviewId(null)}
                    candidateId={profilePreviewId}
                    onChanged={() => load()}
                />
            )}

            {!loading && items.length > 0 && (
                <div className="flex items-center justify-between gap-4 flex-wrap" data-testid="candidates-pagination">
                    <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                        Page {page} of {Math.max(1, Math.ceil(total / pageSize))} · showing {items.length} of {total.toLocaleString()}
                    </div>
                    <div className="flex items-center gap-2">
                        <Button type="button" variant="outline" className="rounded-none h-9" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="candidates-prev-page">← Prev</Button>
                        <Button type="button" variant="outline" className="rounded-none h-9" disabled={page >= Math.ceil(total / pageSize)} onClick={() => setPage((p) => p + 1)} data-testid="candidates-next-page">Next →</Button>
                    </div>
                </div>
            )}
        </div>
    );
}



/** Phase 55.3 — Manual refresh + processing counter.
 *
 * The full-list auto-poll was removed because it caused constant re-render
 * thrash whenever ANY row was processing. This button is always visible and
 * surfaces the current "N processing" count so the recruiter knows there's
 * pending work, without the list refreshing itself.
 */
function RefreshButton({ processingCount, loading, onClick }) {
    return (
        <Button
            onClick={onClick}
            disabled={loading}
            variant="outline"
            size="sm"
            className="rounded-none h-8"
            data-testid="candidates-refresh"
            title={processingCount > 0
                ? `${processingCount} candidate${processingCount === 1 ? "" : "s"} still processing — click to refresh status`
                : "Refresh the candidate list"}
        >
            {loading
                ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            Refresh
            {processingCount > 0 && (
                <span
                    className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-mono font-bold bg-amber-100 text-amber-800 border border-amber-300"
                    data-testid="candidates-processing-count"
                >
                    {processingCount}
                </span>
            )}
        </Button>
    );
}

/** Phase 55.2 — Manually clear candidates stuck in `processing_status='processing'`
 * for longer than 5 minutes. Hits `/candidates/clear-stuck` and refreshes the
 * list. Available to all recruiters/employers so support tickets ("the list
 * keeps reloading!") can be self-resolved without admin or a redeploy.
 */
function ClearStuckButton({ onDone }) {
    const [busy, setBusy] = useState(false);
    const run = async () => {
        if (!window.confirm("Clear candidates stuck in 'processing' for more than 5 minutes? They'll be marked 'failed' with a Retry parse chip so you can re-run on demand.")) return;
        setBusy(true);
        try {
            const { data } = await api.post("/candidates/clear-stuck?older_than_minutes=5");
            if (data.cleared > 0) toast.success(`Cleared ${data.cleared} stuck candidate${data.cleared === 1 ? "" : "s"}.`);
            else toast.success("No stuck candidates found.");
            onDone?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };
    return (
        <Button
            onClick={run}
            disabled={busy}
            variant="outline"
            size="sm"
            className="rounded-none h-8 border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="clear-stuck-btn"
            title="Find candidates frozen in 'Processing' for >5 min and mark them Failed so you can retry"
        >
            <AlertTriangle className="w-3.5 h-3.5 mr-1" />
            {busy ? "Clearing…" : "Clear stuck"}
        </Button>
    );
}
